/**
 * The owner link and what can be done to it.
 *
 * It is the key to the ring, so it appears in two places: the Keys panel, where
 * "who can open this" is decided, and the Share dialog, where every link the
 * ring hands out is listed side by side. One set of controls serves both, so
 * the copy row, the switch and the replace ask the same questions and make the
 * same calls wherever they are pressed.
 *
 * Switching off and replacing are offered only once the calendar is claimed:
 * an unclaimed calendar has no other key, and a person who replaces the one
 * they hold on a device that failed to store the new one is locked out.
 */
import type { Calendar } from '@circle/shared';
import { api } from '../app/api';
import { ownerLink, ownerTokens } from '../app/owner-tokens';
import { button, copyRow, el, withBusy, type Child } from './dom';
import { confirmDialog } from './dialog';
import { toast, toastError } from './toast';

export interface OwnerLinkOptions {
  calendar: Calendar;
  /** The calendar came back changed; the caller keeps it and repaints. */
  onUpdated: (calendar: Calendar) => void;
}

/** The link as a copy row, or the reason there is none to copy. */
export function ownerLinkRow(calendar: Calendar): HTMLElement {
  const token = calendar.ownerToken ?? ownerTokens.get(calendar.id);
  if (!calendar.ownerTokenEnabled) {
    return el('div', { class: 'empty', text: 'The link is switched off. Only your account can edit this ring.' });
  }
  return token
    ? copyRow(ownerLink(calendar.id, token), 'Copy link')
    : el('div', { class: 'empty', text: 'No owner link on this device.' });
}

/** Switch off / on and replace, for a claimed calendar; nothing for an unclaimed one. */
export function ownerLinkActions(options: OwnerLinkOptions): Child[] {
  const { calendar } = options;
  if (!calendar.claimed) return [];
  const enabled = calendar.ownerTokenEnabled;

  const toggle = button(
    enabled ? 'Switch the link off' : 'Switch the link back on',
    () =>
      void withBusy(toggle, async () => {
        try {
          const updated = await api.patchCalendar(calendar.id, { ownerTokenEnabled: !enabled });
          toast(updated.ownerTokenEnabled ? 'Owner link works again.' : 'Owner link switched off.');
          options.onUpdated(updated);
        } catch (err) {
          toastError(err);
        }
      }),
    { class: 'btn--small', 'data-role': 'toggle-owner-link' },
  );

  const rotate = button(
    'Replace the link',
    async () => {
      const yes = await confirmDialog({
        title: 'Replace the owner link?',
        description: 'The old link stops working straight away. Use this if you think someone else has it.',
        confirmLabel: 'Replace link',
      });
      if (!yes) return;
      try {
        const updated = await api.rotateOwnerToken(calendar.id);
        if (updated.ownerToken) ownerTokens.set(calendar.id, updated.ownerToken);
        toast('New owner link ready.');
        options.onUpdated(updated);
      } catch (err) {
        toastError(err);
      }
    },
    { class: 'btn--small btn--quiet', 'data-role': 'rotate-owner-link' },
  );

  return [el('div', { class: 'row' }, toggle, enabled ? rotate : null)];
}
